import type React from 'react';
import {observer} from 'mobx-react-lite';
import {generatePath, useNavigate, useParams} from 'react-router';
import {Alert, Box, Link, Skeleton, Stack, Typography} from '@mui/material';
import {PageHeader} from '~/components/main/header/PageHeader.tsx';
import {routes} from '~/routing/routes';
import {useStores} from '~/stores';
import {useCategorySettingsQuery} from '../hooks/useCategorySettings';
import {BuildsSettings} from './BuildsSettings';
import {ExplainersSection} from './ExplainersSection';
import {MarketDataSettings} from './MarketDataSettings';
import {RiskScoringPanel} from './RiskScoringPanel';
import {SettingsGroupCard} from './SettingsGroupCard';
import {SettingsHistory} from './SettingsHistory';
import {SignalsSettings} from './SignalsSettings';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const WHEN = new Intl.DateTimeFormat(LOCALE, {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'});

/**
 * Category Profile settings, for Mithra staff and customer super users (builds, benchmarks, market index and the
 * build explainer are staff only): thresholds and rules, benchmarks, market indices,
 * the price monitor, external signals and should cost, plus builds and the change history. The server refuses
 * everyone else (`is_staff`); the page does not render for them either.
 */
export const CategorySettingsPage: React.FC = observer(() => {
    const {authStore} = useStores();
    const {id} = useParams<{id: string}>();
    // Category editors open it too: the server gives them the Risk scoring group only
    const canOpen = authStore.isMithraStaff || authStore.isMithraStaffGroup || authStore.isCustomerSuper
        || authStore.hasEditPermission;
    const {data, isLoading, isError, error} = useCategorySettingsQuery(canOpen);
    const refused = (error as {response?: {status?: number}} | null)?.response?.status === 403;
    const navigate = useNavigate();
    // Back link and title live in the app's top bar (Mithra 3.1 header), in every state of the page.
    const header = <PageHeader
        back={{label: 'Explore Categories', onClick: () => navigate(generatePath(routes.category_explore, {id: id ?? ''}))}}
        title="Category Profile settings"
    />;

    if (!canOpen || refused) {
        return <>{header}<Alert severity="warning">Category Profile settings are for users who can edit categories.</Alert></>;
    }
    if (isError) return <>{header}<Alert severity="error">Could not load the settings.</Alert></>;
    if (isLoading || !data) return <Stack gap={2}>{header}<Skeleton height={48}/><Skeleton variant="rounded" height={480}/></Stack>;

    const sections = [...data.groups.map(g => ({key: g.key, label: g.label})),
        ...(data.builds ? [{key: 'builds', label: 'Builds'}] : []),
        {key: 'history', label: 'Change history'}, {key: 'explainers', label: 'Explainers'}];

    return (
        <Stack direction="row" gap={3} alignItems="flex-start">
            {header}
            <Box component="nav" aria-label="Settings sections"
                 sx={{width: 220, flexShrink: 0, position: 'sticky', top: 0, display: {xs: 'none', md: 'block'}}}>
                <Stack gap={0.5}>
                    {sections.map(s => (
                        <Link key={s.key} href={`#settings-${s.key}`} underline="hover" variant="body2" color="text.primary"
                              onClick={e => {
                                  e.preventDefault();
                                  document.getElementById(`settings-${s.key}`)?.scrollIntoView({behavior: 'smooth'});
                              }}>
                            {s.label}
                        </Link>
                    ))}
                </Stack>
            </Box>
            <Stack gap={2} sx={{flex: 1, minWidth: 0, maxWidth: 1100}}>
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        {data.staff ? 'Applies to every dataset in this workspace. Builds, benchmarks and the market index are for Mithra staff only. ' : 'Applies to every dataset in this workspace. '}
                        {data.updatedAt ? `Last changed ${WHEN.format(new Date(data.updatedAt))} by ${data.updatedBy ?? 'unknown'}.`
                            : 'Everything runs on the defaults.'}
                    </Typography>
                </Box>
                {data.groups.map(g => (
                    <SettingsGroupCard key={g.key} group={g} extra={g.key === 'riskscore'
                        ? (values, changes) => <RiskScoringPanel databagId={Number(id)} values={values} changes={changes}/>
                        : undefined}>
                        {g.key === 'market' && data.series && data.providers && (
                            <MarketDataSettings catalogue={{series: data.series, providers: data.providers, uploadedSeries: data.uploadedSeries ?? 0}}/>
                        )}
                        {g.key === 'signals' && <SignalsSettings databagId={Number(id)}/>}
                    </SettingsGroupCard>
                ))}
                {data.builds && <BuildsSettings builds={data.builds}/>}
                <SettingsHistory history={data.history} groups={data.groups}/>
                <ExplainersSection staff={data.staff}/>
            </Stack>
        </Stack>
    );
});
